import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, Triangle, CheckCircle2, XCircle, ThumbsUp, ThumbsDown, Lightbulb } from 'lucide-react';
import Header from '../components/newHeader';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell, Sector,
  RadialBarChart, RadialBar
} from 'recharts';
import logoReport from '../assets/logo_report.png';

// --- 5단계 컬러 시스템 ---
const LEVEL_SOLID_COLORS = ['#EAB308', '#059669', '#2563EB', '#EA580C', '#DC2626'];
const LEVEL_PASTEL_COLORS = [
  'rgba(253,224,71,0.7)', 'rgba(134,239,172,0.7)', 'rgba(147,197,253,0.7)',
  'rgba(253,186,116,0.7)', 'rgba(252,165,165,0.7)'
];

const getRatingColor = (rating) => {
  if (rating <= 0) return '#9CA3AF';
  if (rating <= 2000) return '#EAB308';
  if (rating <= 4000) return '#059669';
  if (rating <= 6000) return '#2563EB';
  if (rating <= 8000) return '#EA580C';
  return '#DC2626';
};

const getDifficultyColor = (d) => {
  if (d <= 0) return '#9CA3AF';
  if (d <= 1) return '#EAB308';
  if (d <= 2) return '#059669';
  if (d <= 3) return '#2563EB';
  if (d <= 4) return '#EA580C';
  return '#DC2626';
};

const getDifficultyPastelColor = (d) => {
  if (d <= 0) return 'rgba(156,163,175,0.4)';
  if (d <= 1) return 'rgba(253,224,71,0.5)';
  if (d <= 2) return 'rgba(134,239,172,0.5)';
  if (d <= 3) return 'rgba(147,197,253,0.5)';
  if (d <= 4) return 'rgba(253,186,116,0.5)';
  return 'rgba(252,165,165,0.5)';
};

// --- renderActiveShape 함수 ---
const renderActiveShape = (props) => {
  const RADIAN = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  const cornerRadius = 8;

  return (
    <g>
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#333">
        <tspan x={cx} dy="-0.6em" fontSize={12} fontWeight="bold">{payload.name}</tspan>
        <tspan x={cx} dy="1.2em" fontSize={10}>{`${value}개`}</tspan>
        <tspan x={cx} dy="1.2em" fontSize={10}>{`(${(percent * 100).toFixed(0)}%)`}</tspan>
      </text>
      <Sector
        cx={cx} cy={cy}
        innerRadius={innerRadius} outerRadius={outerRadius}
        startAngle={startAngle} endAngle={endAngle}
        fill={fill} cornerRadius={cornerRadius}
      />
      <Sector
        cx={cx} cy={cy}
        startAngle={startAngle + 5} endAngle={endAngle - 5}
        innerRadius={outerRadius + 4} outerRadius={outerRadius + 8}
        fill={fill} cornerRadius={cornerRadius}
      />
    </g>
  );
};

function MyReportPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [examInfo, setExamInfo] = useState(null);
  const [fieldInfo, setFieldInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [activeIndexLevel, setActiveIndexLevel] = useState(null);
  const [activeIndexDomain, setActiveIndexDomain] = useState(null);

  const onLevelPieEnter = useCallback((_, index) => {
    setActiveIndexLevel(index);
  }, [setActiveIndexLevel]);

  const onDomainPieEnter = useCallback((_, index) => {
    setActiveIndexDomain(index);
  }, [setActiveIndexDomain]);

  // --- 데이터 계산 함수들 ---

  const getRatingData = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length === 0) {
      return [];
    }
    const data = reportData.exams.map((exam, index) => ({
      name: index === 0 ? '현재 시험' : '이전 시험',
      rating: exam.rating || 0,
      examId: exam.exam_id,
      examRound: exam.exam_round,
      date: new Date(exam.updated_at).toLocaleDateString()
    }));
    const reversed = data.reverse();
    return reversed.map((item, i) => ({
      ...item,
      change: i > 0 ? reversed[i].rating - reversed[i - 1].rating : null
    }));
  }, [reportData]);

  const getYAxisDomain = useCallback(() => {
    const ratingData = getRatingData();
    if (ratingData.length === 0) return [0, 0];
    const ratingValues = ratingData.map(item => item.rating);
    const minRating = Math.min(...ratingValues);
    const maxRating = Math.max(...ratingValues);
    return [minRating - 50, maxRating + 50];
  }, [getRatingData]);

  const getCurrentRating = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length === 0) return 0;
    return reportData.exams[0].rating;
  }, [reportData]);

  const getRatingChange = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length < 2) return 0;
    return reportData.exams[0].rating - reportData.exams[1].rating;
  }, [reportData]);

  const getAccuracyData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.recent_record.by_step) {
      return { accuracy: 0, correctCount: 0, totalCount: 0 };
    }
    const correctByLevel = reportData.recent_record.by_step[0] || [];
    const incorrectByLevel = reportData.recent_record.by_step[1] || [];
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    const totalQuestions = totalCorrect + totalIncorrect;
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    return { accuracy, correctCount: totalCorrect, totalCount: totalQuestions };
  }, [reportData]);

  const getCumulativeAccuracyData = useCallback(() => {
    if (!reportData || !reportData.past_record || !reportData.past_record.by_step) {
      return { accuracy: 0, correctCount: 0, totalCount: 0 };
    }
    const correctByLevel = reportData.past_record.by_step[0] || [];
    const incorrectByLevel = reportData.past_record.by_step[1] || [];
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    const totalQuestions = totalCorrect + totalIncorrect;
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    return { accuracy, correctCount: totalCorrect, totalCount: totalQuestions };
  }, [reportData]);

  const getStrengthsAndWeaknesses = useCallback(() => {
    if (!reportData || !reportData.opinions) {
      return { strengths: [], weaknesses: [] };
    }
    const strengths = [];
    const weaknesses = [];
    if (reportData.opinions.pros) {
      strengths.push({ description: reportData.opinions.pros });
    }
    if (reportData.opinions.cons) {
      weaknesses.push({ description: reportData.opinions.cons });
    }
    return { strengths, weaknesses };
  }, [reportData]);

  const getStudyAreas = useCallback(() => {
    if (!reportData || !reportData.opinions || !reportData.opinions.next_step) return [];
    return Object.entries(reportData.opinions.next_step).map(([title, topics]) => ({
      title,
      topics: Array.isArray(topics) ? topics : []
    }));
  }, [reportData]);

  const getLearningGoals = useCallback(() => {
    if (!reportData || !reportData.opinions) {
      return { goals: [], overallComment: '' };
    }
    const comparison = reportData.opinions.comparison || {};
    const goals = Object.entries(comparison).map(([title, description]) => ({
      title, description, period: '1 - 2주'
    }));
    const overallComment = reportData.opinions.summary || '';
    return { goals, overallComment };
  }, [reportData]);

  const getDetailedAnalysisData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.past_record || !reportData.ref) return [];
    const recentData = reportData.recent_record.by_address || {};
    const pastData = reportData.past_record.by_address || {};
    const refData = reportData.ref || {};
    const allAddressKeys = [...new Set([...Object.keys(recentData), ...Object.keys(pastData)])];

    return allAddressKeys.map(address => {
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];
      const recentCorrect = recent[0] || 0;
      const recentIncorrect = recent[1] || 0;
      const recentTotal = recentCorrect + recentIncorrect;
      const pastCorrect = past[0] || 0;
      const pastIncorrect = past[1] || 0;
      const pastTotal = pastCorrect + pastIncorrect;

      const recentAccuracy = recentTotal > 0
        ? `${Math.round((recentCorrect / recentTotal) * 100)}% (${recentCorrect}/${recentTotal})`
        : '0% (0/0)';
      const cumulativeAccuracy = pastTotal > 0
        ? `${Math.round((pastCorrect / pastTotal) * 100)}% (${pastCorrect}/${pastTotal})`
        : '0% (0/0)';

      const totalCorrect = recentCorrect + pastCorrect;
      const totalIncorrect = recentIncorrect + pastIncorrect;
      const correctDots = Array(totalCorrect).fill('correct');
      const incorrectDots = Array(totalIncorrect).fill('incorrect');
      const allDots = [...correctDots, ...incorrectDots];
      const subCategory = refData[address] || address;

      return {
        address, subCategory, accuracy: recentAccuracy, cumulativeAccuracy,
        correctDots, incorrectDots, allDots,
        category: address.split('/').slice(0, 2).join('/')
      };
    }).sort((a, b) => a.category.localeCompare(b.category));
  }, [reportData]);

  const getDomainDonutData = useCallback(() => {
    if (!reportData || !reportData.ref) return [];
    const recentData = reportData.recent_record?.by_address || {};
    const pastData = reportData.past_record?.by_address || {};
    const refData = reportData.ref || {};
    const allAddressKeys = [...new Set([...Object.keys(recentData), ...Object.keys(pastData)])];
    const subjectMap = new Map();

    allAddressKeys.forEach(address => {
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];
      const totalCount = (recent[0] + recent[1]) + (past[0] + past[1]);
      const subjectName = refData[address] || address;
      if (!subjectMap.has(subjectName)) subjectMap.set(subjectName, 0);
      subjectMap.set(subjectName, subjectMap.get(subjectName) + totalCount);
    });

    return Array.from(subjectMap.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  }, [reportData]);

  const getSubjectRadarData = useCallback(() => {
    if (!reportData) return [];
    const recentData = reportData.recent_record?.by_address || {};
    const pastData = reportData.past_record?.by_address || {};
    const refData = reportData.ref || {};
    const allAddressKeys = [...new Set([...Object.keys(recentData), ...Object.keys(pastData)])];
    const subjectMap = new Map();

    allAddressKeys.forEach(address => {
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];
      const totalCorrect = recent[0] + past[0];
      const totalIncorrect = recent[1] + past[1];
      const totalCount = totalCorrect + totalIncorrect;
      const accuracy = totalCount > 0 ? Math.round((totalCorrect / totalCount) * 100) : 0;
      const subjectName = refData[address] || address;
      subjectMap.set(subjectName, accuracy);
    });

    return Array.from(subjectMap.entries())
      .map(([subject, A]) => ({ subject, A, fullMark: 100 }));
  }, [reportData]);

  const getLevelAchievementData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.recent_record.by_step ||
      !reportData.past_record || !reportData.past_record.by_step) {
      return [1, 2, 3, 4, 5].map(level => ({
        level, currentAcc: 0, currentCorrect: 0, currentTotal: 0,
        cumulativeAcc: 0, cumulativeCorrect: 0, cumulativeTotal: 0
      }));
    }

    const recentCorrect = reportData.recent_record.by_step[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.recent_record.by_step[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.past_record.by_step[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.past_record.by_step[1] || [0, 0, 0, 0, 0];

    return [1, 2, 3, 4, 5].map((level, index) => {
      const cc = recentCorrect[index] || 0;
      const ci = recentIncorrect[index] || 0;
      const ct = cc + ci;
      const ca = ct > 0 ? Math.round((cc / ct) * 100) : 0;
      const pc = pastCorrect[index] || 0;
      const pi = pastIncorrect[index] || 0;
      const pt = pc + pi;
      const pa = pt > 0 ? Math.round((pc / pt) * 100) : 0;
      return { level, currentAcc: ca, currentCorrect: cc, currentTotal: ct, cumulativeAcc: pa, cumulativeCorrect: pc, cumulativeTotal: pt };
    });
  }, [reportData]);

  const getAverageDifficulty = useCallback(() => {
    if (!reportData || !reportData.exams || reportData.exams.length === 0) return 0;
    return reportData.exams[0].difficulty / 100;
  }, [reportData]);

  const getCumulativeDifficulty = useCallback(() => {
    if (!reportData || !reportData.past_avg_difficulty) return 0;
    return reportData.past_avg_difficulty / 100;
  }, [reportData]);

  const getDifficultyLevelText = useCallback((difficulty) => {
    if (difficulty <= 1.5) return "하";
    if (difficulty <= 2.5) return "중하";
    if (difficulty <= 3.5) return "중";
    if (difficulty <= 4.5) return "중상";
    return "상";
  }, []);

  // 도넛 차트 색상
  const LEVEL_GREEN_COLORS = ['#36916E', '#4ABC85', '#7ED0A5', '#A2E4C1', '#C5F1D8'];
  const DOMAIN_GREEN_COLORS = ['#36916E', '#4ABC85', '#7ED0A5', '#A2E4C1', '#C5F1D8', '#2C735A', '#5ED1A0', '#8BE7BF'];

  const levelLegend = [
    { name: 'LV 1', color: LEVEL_PASTEL_COLORS[0], solidColor: LEVEL_SOLID_COLORS[0] },
    { name: 'LV 2', color: LEVEL_PASTEL_COLORS[1], solidColor: LEVEL_SOLID_COLORS[1] },
    { name: 'LV 3', color: LEVEL_PASTEL_COLORS[2], solidColor: LEVEL_SOLID_COLORS[2] },
    { name: 'LV 4', color: LEVEL_PASTEL_COLORS[3], solidColor: LEVEL_SOLID_COLORS[3] },
    { name: 'LV 5', color: LEVEL_PASTEL_COLORS[4], solidColor: LEVEL_SOLID_COLORS[4] }
  ];

  const getLevelDonutData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.past_record) return [];
    const recentCorrect = reportData.recent_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.recent_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.past_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.past_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const totalCorrect = recentCorrect.map((count, i) => count + pastCorrect[i]);
    const totalIncorrect = recentIncorrect.map((count, i) => count + pastIncorrect[i]);
    const totalQuestions = totalCorrect.map((count, i) => count + totalIncorrect[i]);
    return [1, 2, 3, 4, 5].map((level, index) => ({
      name: `LV ${level}`, value: totalQuestions[index] || 0
    })).filter(item => item.value > 0);
  }, [reportData]);

  const levelRadarData = useCallback(() => {
    if (!reportData || !reportData.recent_record || !reportData.past_record) return [];
    const recentCorrect = reportData.recent_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.recent_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.past_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.past_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const totalCorrect = recentCorrect.map((count, i) => count + pastCorrect[i]);
    const totalIncorrect = recentIncorrect.map((count, i) => count + pastIncorrect[i]);

    return [1, 2, 3, 4, 5].map((level, index) => {
      const correct = totalCorrect[index] || 0;
      const incorrect = totalIncorrect[index] || 0;
      const total = correct + incorrect;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
      return { subject: `LV ${level}`, A: accuracy, fullMark: 100 };
    });
  }, [reportData]);

  // --- API 호출 ---
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    const name = localStorage.getItem('student_name');
    const email = localStorage.getItem('student_email');

    if (!storedToken || !name || !email) {
      navigate('/', { replace: true });
      return;
    }

    setToken(storedToken);

    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const reportResponse = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/student/report`,
          {
            params: { exam_id: code },
            headers: { 'Authorization': `Bearer ${storedToken}` }
          }
        );

        if (reportResponse.data.status === 'success' && reportResponse.data.status_code === 200 && isMounted) {
          const apiData = reportResponse.data.data;
          setReportData(apiData);
          setReportError(false);

          if (apiData?.exams && apiData.exams.length > 0) {
            const latestExam = apiData.exams[0];
            setExamInfo({
              exam_type: latestExam.exam_type || '기타',
              updated_at: latestExam.updated_at || new Date().toISOString(),
              exam_count: latestExam.exam_round || 0,
              exam_id: latestExam.exam_id || code,
              difficulty: latestExam.difficulty || 0,
              length: latestExam.length || 0,
            });
          }

          setFieldInfo({
            tree_name: apiData.tree_name || '과목 정보 없음'
          });
        } else {
          if (reportResponse.data.status !== 'success' || reportResponse.data.status_code !== 200) {
            setReportError(true);
            setErrorMessage(reportResponse.data.message || '');
          }
        }
      } catch (error) {
        console.error('데이터를 불러오는데 실패했습니다:', error);
        setReportError(true);
        setErrorMessage(error.response?.data?.message || '리포트를 불러오는데 실패했습니다.');
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate('/', { replace: true });
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    if (storedToken) {
      fetchData();
    } else {
      setIsLoading(false);
    }

    return () => { isMounted = false; };
  }, [code, navigate]);

  const formatExamDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(2);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (e) {
      return '';
    }
  };

  const handleBack = () => {
    navigate(`/myresult/${code}`);
  };

  // --- 난이도 세그먼트 바 컴포넌트 (연속 바) ---
  const DifficultySegmentBar = ({ difficulty }) => {
    const clampedVal = Math.min(5, Math.max(0, difficulty));
    const filledPercent = (clampedVal / 5) * 100;
    const segments = [1, 2, 3, 4, 5];
    return (
      <div className="relative h-2 overflow-hidden rounded-full bg-gray-200">
        <div className="absolute inset-y-0 left-0 flex" style={{ width: `${filledPercent}%` }}>
          {segments.map((seg) => {
            if ((seg - 1) >= clampedVal) return null;
            return (
              <div
                key={seg}
                className="h-full"
                style={{
                  flex: Math.min(1, clampedVal - (seg - 1)),
                  backgroundColor: getDifficultyPastelColor(seg)
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // --- 강점/약점 구조화된 텍스트 렌더링 ---
  const renderStructuredText = (text, type) => {
    if (!text) return null;
    const sentences = text.split(/(?<=다\.|요\.|시오\.|니다\.|세요\.|습니다\.)\s*/);
    if (sentences.length === 0) return null;

    const summary = sentences[0];
    const details = sentences.length > 2 ? sentences.slice(1, -1) : sentences.slice(1);
    const lastSentence = sentences.length > 2 ? sentences[sentences.length - 1] : null;

    const isStudyTip = lastSentence && /추천|학습|연습|반복|풀어|공부/.test(lastSentence);
    const borderColor = type === 'strength' ? 'border-emerald-400' : 'border-red-400';
    const bulletColor = type === 'strength' ? 'bg-emerald-300' : 'bg-red-300';
    const tipBg = type === 'strength' ? 'bg-emerald-50' : 'bg-red-50';

    return (
      <div className={`rounded-r-3xl border-l-4 ${borderColor} bg-gray-50 p-3 space-y-2`}>
        <p className="text-[12px] font-semibold text-headerBg">{summary}</p>
        {details.length > 0 && (
          <ul className="space-y-1">
            {details.map((sentence, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-gray-600">
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${bulletColor}`} />
                {sentence}
              </li>
            ))}
          </ul>
        )}
        {isStudyTip && lastSentence && (
          <div className={`flex items-start gap-2 rounded-xl ${tipBg} p-2`}>
            <Lightbulb size={14} className="mt-0.5 shrink-0 text-amber-500" />
            <p className="text-[11px] text-gray-700">{lastSentence}</p>
          </div>
        )}
      </div>
    );
  };

  // --- 키워드 하이라이팅 ---
  const highlightKeywords = (text) => {
    if (!text) return null;
    return text.split(/('.*?')/g).map((part, i) =>
      part.startsWith("'") && part.endsWith("'")
        ? <span key={i} className="inline-block rounded-full bg-green-50/50 px-1.5 py-0.5 text-[11px] font-bold text-primary">{part.slice(1, -1)}</span>
        : part
    );
  };

  // --- AreaChart 그라데이션 계산 (파스텔톤) ---
  const RATING_PASTEL_COLORS = {
    0: 'rgba(156,163,175,0.35)',
    2000: 'rgba(253,224,71,0.35)',
    4000: 'rgba(134,239,172,0.35)',
    6000: 'rgba(147,197,253,0.35)',
    8000: 'rgba(253,186,116,0.35)',
    10000: 'rgba(252,165,165,0.35)'
  };

  const getRatingGradientStops = useCallback(() => {
    const [minY, maxY] = getYAxisDomain();
    const range = maxY - minY;
    if (range <= 0) return [{ offset: '0%', color: 'rgba(156,163,175,0.35)' }];

    const thresholds = [
      { value: 0, color: 'rgba(156,163,175,0.35)' },
      { value: 2000, color: 'rgba(253,224,71,0.35)' },
      { value: 4000, color: 'rgba(134,239,172,0.35)' },
      { value: 6000, color: 'rgba(147,197,253,0.35)' },
      { value: 8000, color: 'rgba(253,186,116,0.35)' },
      { value: 10000, color: 'rgba(252,165,165,0.35)' }
    ];

    const stops = [];
    thresholds.forEach(({ value, color }) => {
      const pct = ((maxY - value) / range) * 100;
      if (pct >= 0 && pct <= 100) {
        stops.push({ offset: `${pct}%`, color });
      }
    });

    if (stops.length === 0) {
      const rating = getRatingData()[0]?.rating || 0;
      const key = rating <= 0 ? 0 : rating <= 2000 ? 2000 : rating <= 4000 ? 4000 : rating <= 6000 ? 6000 : rating <= 8000 ? 8000 : 10000;
      return [{ offset: '0%', color: RATING_PASTEL_COLORS[key] }];
    }

    stops.sort((a, b) => parseFloat(a.offset) - parseFloat(b.offset));
    return stops;
  }, [getYAxisDomain, getRatingData]);

  // --- Y축 100단위 커스텀 ticks ---
  const getYAxisTicks = useCallback(() => {
    const [minY, maxY] = getYAxisDomain();
    if (minY === 0 && maxY === 0) return [0];
    const start = Math.floor(minY / 100) * 100;
    const end = Math.ceil(maxY / 100) * 100;
    const ticks = [];
    for (let v = start; v <= end; v += 100) {
      ticks.push(v);
    }
    return ticks;
  }, [getYAxisDomain]);

  // --- RadialBar용 데이터 ---
  const getRadialBarData = useCallback(() => {
    const data = levelRadarData();
    return [...data].reverse().map((item, index) => ({
      ...item,
      fill: LEVEL_PASTEL_COLORS[4 - index]
    }));
  }, [levelRadarData]);

  // --- 로딩 상태 ---
  if (isLoading) return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-700 font-medium animate-pulse">로딩중...</p>
          </div>
        </main>
      </div>
    </div>
  );

  // --- 에러 상태 ---
  if (reportError) return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-[#4ABC85] text-2xl">!</span>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">리포트를 생성 중입니다.</h2>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-[#4ABC85] text-white rounded-lg hover:bg-[#3da575] mt-4"
            >
              뒤로 가기
            </button>
          </div>
        </main>
      </div>
    </div>
  );

  // --- 메인 렌더링 ---
  return (
    <div className="relative flex flex-col min-h-screen">
      <div className="fixed inset-0 bg-[#FAFAFA] z-0" />
      <div className="relative z-20 flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex flex-col items-center pt-[84px] pb-[120px] px-4">
          <div className="max-w-[700px] w-full mx-auto">
            {/* 헤더 */}
            <div className="flex items-center justify-center mb-6 relative">
              <button
                onClick={handleBack}
                className="absolute left-0 flex items-center text-gray-600 hover:text-gray-800"
              >
                <ChevronLeft size={24} />
              </button>
              <h1 className="text-[18px] font-black text-[#4ABC85]">AI</h1>
              <h1 className="text-[18px] font-black text-[#231F20] pl-1">리포트</h1>
            </div>

            {/* 시험 정보 카드 */}
            <div className="w-full rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white mb-[20px]">
              {examInfo && fieldInfo ? (
                <div className="flex flex-col">
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-[#231F20]">{fieldInfo.tree_name || '과목명'}</span>
                      <span className="w-[38px] h-[16px] flex items-center justify-center bg-[#4ABC85] text-[#FFFFFF] rounded-full text-[8px]">
                        {examInfo.exam_type}
                      </span>
                    </div>
                    <span className="text-[12px] text-[#A3A1A2]">{formatExamDate(examInfo.updated_at)}</span>
                  </div>
                  <div className="w-full bg-[#f1f1f1] rounded-3xl flex items-center justify-center px-4 h-[50px]">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <span className="text-[#231F20]/60 text-[11px]">회차</span>
                        <span className="text-[#231F20] text-[11px]">{examInfo.exam_count}</span>
                        <span className="text-[#231F20]/60 text-[8px]">· TID</span>
                        <span className="text-[#231F20] text-[8px]">{examInfo.exam_id}</span>
                      </div>
                      <div className="h-3 w-[1px] bg-[#cccccc]"></div>
                      <div className="flex items-center gap-1">
                        <span className="text-[#231F20]/60 text-[11px]">난이도</span>
                        <span className="text-[#231F20] font-medium text-[11px]">{(examInfo.difficulty / 100).toFixed(2)}</span>
                      </div>
                      <div className="h-3 w-[1px] bg-[#cccccc]"></div>
                      <div className="flex items-center gap-1">
                        <span className="text-[#231F20]/60 text-[11px]">문제 수</span>
                        <span className="text-[#231F20] font-medium text-[11px]">{examInfo.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="h-[72px] flex items-center justify-center">
                  <p className="text-gray-500">시험 정보 로딩 중...</p>
                </div>
              )}
            </div>

            {/* 테스트 성적 요약 카드 */}
            <div className="w-full rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white mb-4">
              <h2 className="text-base font-bold mb-4 text-[#231F20]">테스트 성적 요약</h2>

              {/* 현재 폼레이팅 (전체 너비) */}
              <div className="rounded-3xl bg-gray-50 p-4 flex flex-col items-center mb-3">
                <span className="text-[13px] font-semibold text-[#231F20] mb-1">현재 폼레이팅</span>
                <p className="text-[28px] font-bold mb-2" style={{ color: getRatingColor(getCurrentRating()) }}>
                  {getCurrentRating().toLocaleString()}
                </p>
                <div className="flex items-center">
                  <div className="flex items-center rounded-3xl border border-gray-200 h-[22px] px-2.5">
                    <Triangle
                      className={`w-2 h-2 mr-0.5 fill-current ${getRatingChange() < 0 ? 'text-blue-600 rotate-180' : 'text-red-600 rotate-0'}`}
                    />
                    <span className="text-[10px] font-semibold text-[#231F20]">{Math.abs(getRatingChange())}</span>
                  </div>
                </div>
              </div>

              {/* 정답률 카드 */}
              <div className="rounded-3xl bg-gray-50 p-4 mb-3">
                <span className="text-[13px] font-semibold text-[#231F20] mb-3 block">정답률</span>
                {/* 현재 행 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] text-gray-500 w-8 shrink-0">현재</span>
                  <span className="text-[14px] font-bold text-[#231F20] w-10 text-right">{getAccuracyData().accuracy}%</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-primary h-full rounded-full" style={{ width: `${getAccuracyData().accuracy}%` }} />
                  </div>
                  <span className="text-[10px] font-semibold rounded-3xl bg-gray-100 w-16 text-center py-0.5 shrink-0">
                    <span className="text-primary">{getAccuracyData().correctCount}</span><span className="text-gray-300">/</span><span className="text-[#231F20]">{getAccuracyData().totalCount}</span>
                  </span>
                </div>
                {/* 누적 행 */}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-500 w-8 shrink-0">누적</span>
                  <span className="text-[14px] font-bold text-gray-400 w-10 text-right">{getCumulativeAccuracyData().accuracy}%</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div className="bg-headerBg/30 h-full rounded-full" style={{ width: `${getCumulativeAccuracyData().accuracy}%` }} />
                  </div>
                  <span className="text-[10px] font-semibold rounded-3xl bg-gray-100 w-16 text-center py-0.5 shrink-0">
                    <span className="text-gray-400">{getCumulativeAccuracyData().correctCount}</span><span className="text-gray-300">/</span><span className="text-[#231F20]">{getCumulativeAccuracyData().totalCount}</span>
                  </span>
                </div>
              </div>

              {/* 난이도 카드 */}
              <div className="rounded-3xl bg-gray-50 p-4">
                <span className="text-[13px] font-semibold text-[#231F20] mb-3 block">난이도</span>
                {/* 현재 행 */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] text-gray-500 w-8 shrink-0">현재</span>
                  <span className="text-[14px] font-bold w-10 text-right" style={{ color: getDifficultyColor(getAverageDifficulty()) }}>
                    {getAverageDifficulty().toFixed(2)}
                  </span>
                  <div className="flex-1">
                    <DifficultySegmentBar difficulty={getAverageDifficulty()} />
                  </div>
                  <span className="text-[10px] font-semibold rounded-3xl bg-gray-100 w-16 text-center py-0.5 shrink-0" style={{ color: getDifficultyColor(getAverageDifficulty()) }}>
                    {getDifficultyLevelText(getAverageDifficulty())}
                  </span>
                </div>
                {/* 누적 행 */}
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-500 w-8 shrink-0">누적</span>
                  <span className="text-[14px] font-bold text-gray-400 w-10 text-right">
                    {getCumulativeDifficulty().toFixed(2)}
                  </span>
                  <div className="flex-1">
                    <DifficultySegmentBar difficulty={getCumulativeDifficulty()} />
                  </div>
                  <span className="text-[10px] font-semibold text-gray-400 rounded-3xl bg-gray-100 w-16 text-center py-0.5 shrink-0">
                    {getDifficultyLevelText(getCumulativeDifficulty())}
                  </span>
                </div>
              </div>
            </div>

            {/* === 그리드 레이아웃 시작 === */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">

              {/* 1. 폼레이팅 변화 추이 (4칸) */}
              <div className="col-span-1 sm:col-span-2 md:col-span-4 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">폼레이팅 변화 추이</h3>
                <div className="relative h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={getRatingData()}
                      margin={{ top: 5, right: 10, left: 0, bottom: 20 }}
                    >
                      <defs>
                        <linearGradient id="ratingGradient" x1="0" y1="0" x2="0" y2="1">
                          {getRatingGradientStops().map((stop, i) => (
                            <stop key={i} offset={stop.offset} stopColor={stop.color} stopOpacity={0.8} />
                          ))}
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        domain={getYAxisDomain()}
                        ticks={getYAxisTicks()}
                        width={45}
                        tick={({ x, y, payload }) => (
                          <text x={x} y={y} textAnchor="end" fontSize={10} fontWeight="bold" fill={getRatingColor(payload.value)}>
                            {payload.value.toLocaleString()}
                          </text>
                        )}
                      />
                      <XAxis
                        dataKey="examRound"
                        axisLine={false}
                        tickLine={false}
                        tick={({ x, y, payload }) => (
                          <g transform={`translate(${x},${y})`}>
                            <text x={0} y={0} dy={8} textAnchor="middle" fontSize={11} fill="#231F20" fontWeight="600">
                              {payload.value}
                            </text>
                            <text x={0} y={0} dy={20} textAnchor="middle" fontSize={9} fill="#A3A1A2">
                              회차
                            </text>
                          </g>
                        )}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload[0]) return null;
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-xl bg-white p-2.5 shadow-lg border border-gray-100 text-xs">
                              <p className="text-gray-500 mb-1">{data.examRound}회차 · {data.date}</p>
                              <p className="font-bold" style={{ color: getRatingColor(data.rating) }}>
                                {data.rating.toLocaleString()}
                              </p>
                              {data.change !== null && (
                                <p className={`text-[10px] ${data.change >= 0 ? 'text-red-500' : 'text-blue-500'}`}>
                                  {data.change >= 0 ? '▲' : '▼'} {Math.abs(data.change)}
                                </p>
                              )}
                            </div>
                          );
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="rating"
                        stroke="#4ABC85"
                        strokeWidth={2}
                        fill="url(#ratingGradient)"
                        dot={({ cx, cy, payload }) => (
                          <circle key={`dot-${payload.examRound}`} cx={cx} cy={cy} r={2.5} fill={getRatingColor(payload.rating)} stroke="white" strokeWidth={1.5} />
                        )}
                        activeDot={({ cx, cy, payload }) => (
                          <circle key={`adot-${payload.examRound}`} cx={cx} cy={cy} r={4} fill={getRatingColor(payload.rating)} stroke="white" strokeWidth={1.5} />
                        )}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 2. 난이도별 정답률 바 (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-[14px] font-bold text-[#231F20]">난이도별 정답률</h3>
                  <div className="flex space-x-3">
                    <div className="flex items-center">
                      <div className="w-2.5 h-2.5 bg-[#4ABC85] rounded-sm mr-1"></div>
                      <span className="text-[10px] text-[#A3A1A2]">현재</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2.5 h-2.5 bg-[#A3A1A2] rounded-sm mr-1"></div>
                      <span className="text-[10px] text-[#A3A1A2]">누적</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-3xl bg-gray-50 p-3 space-y-3">
                  {getLevelAchievementData().map(item => (
                    <div key={item.level} className="space-y-1">
                      <div className="flex items-center gap-2 text-[12px]">
                        <span className="w-8 shrink-0 font-bold" style={{ color: LEVEL_SOLID_COLORS[item.level - 1] }}>Lv {item.level}</span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div className="bg-[#4ABC85] h-full rounded-full" style={{ width: `${item.currentAcc}%` }} />
                        </div>
                        <span className="w-9 shrink-0 text-right font-bold" style={{ color: LEVEL_SOLID_COLORS[item.level - 1] }}>{item.currentAcc}%</span>
                        <span className="w-12 shrink-0 text-right text-[10px] font-semibold" style={{ color: LEVEL_SOLID_COLORS[item.level - 1] }}>{item.currentCorrect}/{item.currentTotal}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[12px]">
                        <span className="w-8 shrink-0" />
                        <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div className="bg-[#A3A1A2] h-full rounded-full" style={{ width: `${item.cumulativeAcc}%` }} />
                        </div>
                        <span className="w-9 shrink-0 text-right text-[#A3A1A2]">{item.cumulativeAcc}%</span>
                        <span className="w-12 shrink-0 text-right text-[10px] text-gray-400">{item.cumulativeCorrect}/{item.cumulativeTotal}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2. 유형별 정답률 테이블 (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">유형별 정답률</h3>
                <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-2 px-2 text-left font-medium text-[#7B7979] whitespace-nowrap">소단원</th>
                        <th className="py-2 px-2 text-left font-medium text-[#7B7979] whitespace-nowrap">정답률</th>
                        <th className="py-2 px-2 text-left font-medium text-[#7B7979] whitespace-nowrap">누적</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getDetailedAnalysisData().map((item, index) => {
                        const accuracyMatch = item.accuracy.match(/(\d+)%\s*\((\d+)\/(\d+)\)/);
                        const cumulativeMatch = item.cumulativeAccuracy.match(/(\d+)%\s*\((\d+)\/(\d+)\)/);
                        return (
                          <tr key={index} className={`${index === getDetailedAnalysisData().length - 1 ? '' : 'border-b'} border-gray-200`}>
                            <td className="py-3 px-2 align-middle text-[#231F20] whitespace-nowrap overflow-hidden text-ellipsis" style={{ maxWidth: '120px' }}>
                              {item.subCategory}
                            </td>
                            <td className="py-3 px-2 align-middle whitespace-nowrap font-bold">
                              {accuracyMatch ? (
                                <>
                                  <span className="text-[#4ABC85]">{accuracyMatch[2]}</span>
                                  <span className="text-[#231F20]">/{accuracyMatch[3]}</span>
                                  <span className="ml-1 text-[#7B7979] text-[10px]">({accuracyMatch[1]}%)</span>
                                </>
                              ) : item.accuracy}
                            </td>
                            <td className="py-3 px-2 align-middle whitespace-nowrap font-bold">
                              {cumulativeMatch ? (
                                <>
                                  <span className="text-[#A3A1A2]">{cumulativeMatch[2]}</span>
                                  <span className="text-[#231F20]">/{cumulativeMatch[3]}</span>
                                  <span className="ml-1 text-[#7B7979] text-[10px]">({cumulativeMatch[1]}%)</span>
                                </>
                              ) : item.cumulativeAccuracy}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 3. 난이도별 성취도 도넛 (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">난이도별 성취도</h3>
                <div className="h-48 mb-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexLevel}
                        activeShape={renderActiveShape}
                        data={getLevelDonutData()}
                        cx="50%" cy="50%"
                        innerRadius="60%" outerRadius="80%"
                        paddingAngle={5} cornerRadius={8}
                        dataKey="value"
                        onMouseEnter={onLevelPieEnter}
                      >
                        {getLevelDonutData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={LEVEL_PASTEL_COLORS[index % LEVEL_PASTEL_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                  {levelLegend.map((legend, index) => (
                    <div key={legend.name} className="flex items-center">
                      <div className="w-2.5 h-2.5 rounded-full mr-1" style={{ backgroundColor: LEVEL_PASTEL_COLORS[index] }}></div>
                      <span className="text-[10px] font-bold" style={{ color: LEVEL_SOLID_COLORS[index] }}>{legend.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3. 유형별 성취도 도넛 (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">유형별 성취도</h3>
                <div className="h-48 mb-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexDomain}
                        activeShape={renderActiveShape}
                        data={getDomainDonutData()}
                        cx="50%" cy="50%"
                        innerRadius="60%" outerRadius="80%"
                        cornerRadius={8} paddingAngle={5}
                        dataKey="value"
                        onMouseEnter={onDomainPieEnter}
                      >
                        {getDomainDonutData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={DOMAIN_GREEN_COLORS[index % DOMAIN_GREEN_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
                  {getDomainDonutData().map((item, index) => (
                    <div key={item.name} className="flex items-center">
                      <div className="w-2.5 h-2.5 rounded-sm mr-1" style={{ backgroundColor: DOMAIN_GREEN_COLORS[index % DOMAIN_GREEN_COLORS.length] }}></div>
                      <span className="text-[10px] text-[#7B7979]">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 4. 난이도별 정답률 RadialBar (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-[14px] font-bold text-[#231F20]">난이도별 정답률</h3>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      cx="50%" cy="50%"
                      innerRadius="20%" outerRadius="90%"
                      barSize={12}
                      data={getRadialBarData()}
                      startAngle={90} endAngle={-270}
                    >
                      <RadialBar
                        background
                        dataKey="A"
                        cornerRadius={6}
                      >
                        {getRadialBarData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </RadialBar>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload[0]) return null;
                          const data = payload[0].payload;
                          const lvIndex = parseInt(data.subject.replace('LV ', '')) - 1;
                          return (
                            <div className="rounded-xl bg-white p-2 shadow-lg border border-gray-100 text-xs">
                              <span className="font-bold" style={{ color: LEVEL_SOLID_COLORS[lvIndex] }}>{data.subject}</span>
                              <span className="ml-2">{data.A}%</span>
                            </div>
                          );
                        }}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 justify-center mt-2">
                  {levelLegend.map((legend, index) => (
                    <div key={legend.name} className="flex items-center">
                      <div className="w-2.5 h-2.5 rounded-full mr-1" style={{ backgroundColor: LEVEL_PASTEL_COLORS[index] }}></div>
                      <span className="text-[10px] font-bold" style={{ color: LEVEL_SOLID_COLORS[index] }}>{legend.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 4. 주요 유형별 정답률 Radar (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-[14px] font-bold text-[#231F20]">주요 유형별 정답률</h3>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-[#4ABC85] mr-1 rounded-sm"></div>
                    <span className="text-[10px] text-[#7B7979]">정답률 (%)</span>
                  </div>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={getSubjectRadarData()}>
                      <PolarGrid stroke="#E0E0E0" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B7979' }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} axisLine={false} tick={false} />
                      <Radar name="정답률" dataKey="A" stroke="#4ABC85" fill="#4ABC85" fillOpacity={0.6} />
                      <Tooltip
                        contentStyle={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ccc' }}
                        formatter={(value, name, props) => [`${props.payload.subject}: ${value}%`, null]}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 5. 강점 (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsUp size={16} className="text-emerald-500" />
                  <h3 className="text-[14px] font-bold text-[#231F20]">강점</h3>
                </div>
                <div className="space-y-3">
                  {getStrengthsAndWeaknesses().strengths.map((item, index) => (
                    <div key={index}>
                      {renderStructuredText(item.description, 'strength')}
                    </div>
                  ))}
                </div>
              </div>

              {/* 5. 약점 (2칸) */}
              <div className="col-span-1 sm:col-span-1 md:col-span-2 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <ThumbsDown size={16} className="text-red-400" />
                  <h3 className="text-[14px] font-bold text-[#231F20]">약점</h3>
                </div>
                <div className="space-y-3">
                  {getStrengthsAndWeaknesses().weaknesses.map((item, index) => (
                    <div key={index}>
                      {renderStructuredText(item.description, 'weakness')}
                    </div>
                  ))}
                </div>
              </div>

              {/* 6. 공부가 필요한 영역 (4칸) */}
              <div className="col-span-1 sm:col-span-2 md:col-span-4 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <h3 className="text-[14px] font-bold mb-4 text-[#231F20]">공부가 필요한 영역</h3>
                <div className="space-y-4">
                  {getStudyAreas().map((area, index) => (
                    <div key={index} className="rounded-3xl bg-gray-50 p-4">
                      <div className="flex items-center mb-3">
                        <span className="inline-flex items-center justify-center h-[16px] px-2 bg-[#4ABC85] text-white rounded-full text-[8px] font-bold mr-2">
                          CHECK
                        </span>
                        <span className="text-[14px] font-semibold text-[#231F20]">{area.title}</span>
                      </div>
                      <ul className="list-disc list-inside space-y-1.5 text-[12px] text-[#555555]">
                        {area.topics.map((topic, topicIndex) => (
                          <li key={topicIndex}>{topic}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* 7. 개선 방향 및 학습 조언 (4칸) */}
              <div className="col-span-1 sm:col-span-2 md:col-span-4 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] bg-white">
                <h2 className="text-base font-bold mb-5 text-[#231F20]">개선 방향 및 학습 조언</h2>

                {getLearningGoals().goals.map((goal, index) => (
                  <div key={index} className="mb-6">
                    <div className="flex items-center mb-3">
                      <h3 className="text-[14px] font-bold text-[#231F20]">{goal.title}</h3>
                    </div>
                    <div className="rounded-3xl bg-gray-50 p-4">
                      <p className="text-[12px] text-[#231F20] leading-relaxed">{highlightKeywords(goal.description)}</p>
                    </div>
                  </div>
                ))}

                <div>
                  <h3 className="text-[14px] font-bold mb-3 text-[#231F20]">종합 평가 및 코멘트</h3>
                  <div className="rounded-3xl bg-gray-50 p-4 text-[12px] text-[#231F20] leading-relaxed whitespace-pre-line">
                    {highlightKeywords(getLearningGoals().overallComment)}
                  </div>
                </div>
              </div>

            </div>
            {/* === 그리드 레이아웃 끝 === */}

          </div>
        </main>
      </div>
    </div>
  );
}

export default MyReportPage;
